import React, { useEffect, useRef, useState } from 'react';
import { Chart, ChartConfiguration, ChartType } from 'chart.js/auto';
import Header from './Header';
import DocumentationGallery from './DocumentationGallery';
import { CurrencyIcon, PeopleIcon, LocationIcon, CalendarIcon, BoxIcon, PinDotIcon } from './Icons';

const HousingReport: React.FC = () => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);
  const [isVisible] = useState(true);
  const [animatedCards, setAnimatedCards] = useState<number[]>([]);

  const heroHighlights = [
    '200 سلة غذائية مكتملة',
    'توزيع مرحلي دقيق',
    'أولوية الأسر الأشد احتياجاً'
  ];

  useEffect(() => {
    const iconSupport = typeof window !== 'undefined' && typeof CSS !== 'undefined' && CSS.supports('color', 'currentColor');
    if (!iconSupport) {
      document.documentElement.classList.add('icon-fallback');
    }
  }, []);

  const statCards: Array<{
    label: string;
    value: string;
    unit: string;
    icon: React.ReactNode;
    description: string;
  }> = [
    {
      label: 'إجمالي قيمة المساهمة',
      value: '50,000',
      unit: 'ريال سعودي',
      icon: <CurrencyIcon className="icon-md text-brand-secondary" title="إجمالي قيمة المساهمة" size={24} />,
      description: 'استثمار كامل لتوفير سلال غذائية متكاملة للأسر المستفيدة.'
    },
    {
      label: 'عدد المستفيدين',
      value: '200',
      unit: 'أسرة',
      icon: <PeopleIcon className="icon-md text-brand-secondary" title="عدد المستفيدين" size={24} />,
      description: 'استفادت الأسر الأشد احتياجاً المسجلة في مجمع إسكان الملك عبدالله بديحمة.'
    },
    {
      label: 'الموقع',
      value: 'إسكان الملك عبدالله',
      unit: 'بديحمة',
      icon: <LocationIcon className="icon-md text-brand-secondary" title="الموقع المستهدف" size={24} />,
      description: 'تم التوزيع المباشر داخل المجمع السكني مع إشراف ميداني كامل.'
    }
  ];

  const phaseDetails = [
    {
      title: 'المرحلة الأولى',
      date: '25 يونيو 2025م',
      quantity: '150 سلة غذائية',
      description: 'تضمنت تهيئة المستودعات وتوزيع 150 سلة غذائية للأسر الأشد حاجة ضمن القائمة الرئيسية.'
    },
    {
      title: 'المرحلة الثانية',
      date: '23 أكتوبر 2025م',
      quantity: '50 سلة غذائية',
      description: 'استكملت المبادرة بتوزيع 50 سلة إضافية للأسر التي تم تسجيلها في المتابعة الدورية.'
    }
  ];

  const impactHighlights = [
    {
      title: 'التوزيع المرحلي',
      subtitle: 'مرحلتان مكتملتان',
      description: 'تم توزيع 150 سلة في المرحلة الأولى و50 سلة في المرحلة الثانية لضمان الاستمرارية.'
    },
    {
      title: 'جودة الدعم',
      subtitle: 'قيمة السلة 250 ريال',
      description: 'كل سلة تحتوي على مكونات غذائية أساسية تلبي احتياجات الأسرة بشكل متوازن.'
    },
    {
      title: 'المجتمع المستفيد',
      subtitle: '200 أسرة في إسكان ديحمة',
      description: 'استهدفت المبادرة أسر مجمع إسكان الملك عبدالله وفق قوائم موثقة ومحدثة.'
    }
  ];

  useEffect(() => {
    const timers = statCards.map((_, index) =>
      setTimeout(() => {
        setAnimatedCards((prev) => (prev.includes(index) ? prev : [...prev, index]));
      }, index * 180)
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [statCards.length]);

  useEffect(() => {
    if (chartRef.current && isVisible) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        Chart.defaults.font.family = 'Tajawal, sans-serif';

        const chartData = {
          labels: ['المرحلة الأولى (150 سلة)', 'المرحلة الثانية (50 سلة)'],
          datasets: [
            {
              label: 'عدد السلال',
              data: [150, 50],
              backgroundColor: ['#94A3B8', '#CBD5F5'],
              hoverBackgroundColor: ['#64748B', '#9CA3C9'],
              borderColor: '#FFFFFF',
              borderWidth: 4,
              hoverOffset: 14,
              borderRadius: 12,
              spacing: 4
            }
          ]
        };

        const chartOptions = {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '62%',
          plugins: {
            legend: {
              position: 'bottom' as const,
              labels: {
                font: {
                  size: 14,
                  weight: '500' as const,
                  family: 'Tajawal, sans-serif'
                },
                color: '#334155',
                padding: 20,
                usePointStyle: true,
                pointStyle: 'circle'
              }
            },
            tooltip: {
              enabled: true,
              rtl: true,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              titleColor: '#111827',
              bodyColor: '#334155',
              borderColor: '#E2E8F0',
              borderWidth: 1,
              cornerRadius: 14,
              padding: 16,
              displayColors: true,
              bodyFont: {
                size: 14,
                family: 'Tajawal, sans-serif'
              },
              titleFont: {
                size: 16,
                weight: 'bold' as const,
                family: 'Tajawal, sans-serif'
              },
              callbacks: {
                label: function (context: any) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  if (context.parsed !== null) {
                    label += context.parsed + ' سلة';
                  }
                  return label;
                }
              }
            }
          },
          animation: {
            animateScale: true,
            animateRotate: true,
            duration: 2000,
            easing: 'easeInOutQuart' as const
          }
        };

        const config: ChartConfiguration = {
          type: 'doughnut' as ChartType,
          data: chartData,
          options: chartOptions
        };

        chartInstanceRef.current = new Chart(ctx, config);
      }
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [isVisible, statCards.length]);

  return (
    <main className="relative min-h-screen brand-surface-gradient text-brand-primary overflow-hidden" dir="rtl">
      <Header />
      <div className="decorative-layer">
        <div className="decorative-orb orb-one" aria-hidden="true"></div>
        <div className="decorative-orb orb-two" aria-hidden="true"></div>
        <div className="decorative-orb orb-three" aria-hidden="true"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-20 space-y-16">
        <section className={`grid gap-10 lg:gap-16 lg:grid-cols-12 items-stretch transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="lg:col-span-7 space-y-7">
            <div className="inline-flex items-center gap-3 rounded-full bg-brand-glass border border-brand-soft shadow-brand-card px-5 py-2">
              <PinDotIcon className="icon-dot" title="بداية التقرير" />
              <span className="text-sm font-medium text-brand-muted">تقرير المبادرة المجتمعية</span>
            </div>
            <div className="space-y-5">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight text-brand-primary">
                تقرير المساهمات في مجمع إسكان الملك عبدالله
              </h1>
              <p className="text-base md:text-lg text-brand-muted leading-relaxed max-w-2xl">أهلاً بكم شركاؤنا الأعزاء. هذا التقرير التفاعلي يوضح تفاصيل المساهمات المقدمة لمستفيدي مجمع إسكان الملك عبدالله بديحمة ويستعرض رحلة التوزيع الكاملة من التخطيط وحتى المتابعة الميدانية.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2.5 md:gap-3">
              {heroHighlights.map((item) => (
                <span key={item} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-brand-glass border border-brand-soft text-sm font-medium text-brand-secondary shadow-brand-card hover:-translate-y-0.5 transition-transform">
                  <span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ background: 'radial-gradient(circle, rgba(30,109,59,0.45), transparent)' }}></span>
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-4xl bg-brand-glass border border-brand-soft shadow-brand-card p-5 sm:p-7 md:p-8 h-full">
              <div className="absolute -top-24 -left-16 h-56 w-56 rounded-full" style={{ background: 'radial-gradient(circle, rgba(200,157,58,0.28), transparent)' }}></div>
              <div className="absolute -bottom-24 -right-16 h-60 w-60 rounded-full" style={{ background: 'radial-gradient(circle, rgba(30,109,59,0.22), transparent)' }}></div>
              <div className="relative z-10 flex flex-col gap-6 h-full justify-between">
                <div className="absolute -top-14 -right-14 h-32 w-32 rounded-full bg-white/50 blur-2xl"></div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-semibold text-brand-primary">لمحة عن التقرير</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-3xl bg-white/70 border border-brand-soft px-5 py-4 shadow-brand-card">
                      <span className="text-sm text-brand-muted">الموقع</span>
                      <p className="mt-2 text-lg font-semibold text-brand-primary">إسكان الملك عبدالله - بديحمة</p>
                    </div>
                    <div className="rounded-3xl bg-white/70 border border-brand-soft px-5 py-4 shadow-brand-card">
                      <span className="text-sm text-brand-muted">الفترة الزمنية</span>
                      <p className="mt-2 text-lg font-semibold text-brand-primary">يونيو - أكتوبر 2025</p>
                    </div>
                  </div>
                  <div className="rounded-3xl bg-white/80 border border-brand-soft px-5 py-4 shadow-brand-card">
                    <div className="flex items-center justify-between text-sm text-brand-muted mb-3">
                      <span>نسبة الإنجاز</span>
                      <span className="font-semibold text-brand-secondary">100%</span>
                    </div>
                    <div className="h-2 rounded-full bg-brand-glass overflow-hidden border border-brand-soft">
                      <div className="h-full w-full rounded-full" style={{ background: 'linear-gradient(90deg, var(--brand-secondary), var(--brand-primary))' }}></div>
                    </div>
                    <p className="mt-3 text-sm text-brand-muted leading-relaxed">
                      اكتملت مرحلتان متتاليتان بإشراف ميداني مباشر لضمان وصول الدعم إلى كل أسرة مستهدفة وفق معايير دقيقة في التوزيع والمتابعة.
                    </p>
                  </div>
                </div>
                <div className="rounded-3xl bg-white/70 border border-brand-soft px-5 py-4 shadow-brand-card flex items-center justify-between">
                  <div>
                    <p className="text-sm text-brand-muted">آلية التوثيق</p>
                    <p className="mt-1 text-base font-semibold text-brand-primary">تقارير مصوّرة ومراجعة مرحلية</p>
                  </div>
                  <PinDotIcon className="icon-dot" title="مؤشر المتابعة" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {statCards.map((card, index) => (
              <div
                key={card.label}
                className={`relative overflow-hidden rounded-4xl border border-brand-soft bg-brand-glass backdrop-blur-sm p-6 md:p-8 shadow-brand-card transition-all duration-700 ${animatedCards.includes(index) ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
                style={{ transitionDelay: `${index * 160}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[rgba(200,157,58,0.12)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10 space-y-5">
                  <div className="absolute -top-20 -left-20 h-32 w-32 rounded-full opacity-0 group-hover:opacity-75 transition-opacity duration-500" style={{ background: 'radial-gradient(circle, rgba(30,109,59,0.18), transparent)' }} aria-hidden="true"></div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-brand-muted uppercase tracking-[0.18em]">{card.label}</span>
                    <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-glass border border-brand-soft text-brand-secondary group-hover:bg-[var(--brand-secondary)] group-hover:text-white transition-colors duration-500">
                      {card.icon}
                    </span>
                  </div>
                  <p className="text-4xl md:text-5xl font-bold text-brand-primary tracking-tight">{card.value}</p>
                  <span className="text-lg font-medium text-brand-secondary">{card.unit}</span>
                  <p className="text-sm leading-relaxed text-brand-muted">{card.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={`grid gap-12 xl:gap-16 xl:grid-cols-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="xl:col-span-5 space-y-8 md:space-y-10">
            <div className="bg-brand-glass backdrop-blur-sm border border-brand-soft rounded-4xl p-6 md:p-8 shadow-brand-card">
              <h2 className="text-3xl font-bold text-brand-primary mb-4">تفاصيل التنفيذ والمراحل</h2>
              <p className="text-lg leading-relaxed text-brand-muted">
                تم توزيع 200 سلة غذائية عبر مرحلتين رئيسيتين لضمان وصول الدعم إلى جميع الأسر المسجلة في مجمع إسكان الملك عبدالله بديحمة وفق خطة زمنية محكمة.
              </p>
            </div>
            <div className="rounded-4xl bg-brand-glass border border-brand-soft shadow-brand-card p-6 md:p-8">
              <h3 className="text-xl font-semibold text-brand-primary mb-4">الأثر النوعي</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {impactHighlights.map((impact) => (
                  <div key={impact.title} className="rounded-3xl bg-white/70 border border-brand-soft px-4 py-5 shadow-brand-card">
                    <p className="text-xs font-semibold text-brand-muted uppercase tracking-[0.24em] mb-2">{impact.title}</p>
                    <h4 className="text-lg font-bold text-brand-primary mb-2">{impact.subtitle}</h4>
                    <p className="text-sm leading-relaxed text-brand-muted">{impact.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="xl:col-span-7 space-y-8">
            <div className="relative rounded-4xl border border-brand-soft bg-brand-glass p-6 md:p-8 lg:p-10 shadow-brand-card backdrop-blur-sm">
              <div className="absolute inset-0 rounded-4xl bg-gradient-to-br from-[rgba(200,157,58,0.16)] to-transparent">
                <div className="absolute inset-6 md:inset-8 rounded-3xl border border-white/35" aria-hidden="true"></div>
              </div>
              <div className="relative z-10">
                <h3 className="mb-8 text-center text-xl font-semibold text-brand-secondary">توزيع السلال حسب المرحلة</h3>
                <div className="chart-container" style={{ position: 'relative', width: '100%', height: '420px' }}>
                  <canvas ref={chartRef}></canvas>
                </div>
              </div>
            </div>

            <div className="relative border-r border-dashed border-brand-soft pr-6 sm:pr-8">
              <div className="absolute right-[-1.3rem] top-0 bottom-0 hidden sm:flex flex-col items-center" aria-hidden="true">
                <div className="w-2 h-2 rounded-full bg-brand-secondary/90"></div>
                <div className="flex-1 w-[1.5px] bg-gradient-to-b from-brand-secondary/70 via-transparent to-transparent"></div>
                <div className="w-2 h-2 rounded-full bg-brand-secondary/40"></div>
              </div>
              {phaseDetails.map((phase, index) => (
                <div key={phase.title} className="relative pl-6 sm:pl-8 mb-10 last:mb-0">
                  <span className="absolute -right-4 top-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-sm font-semibold text-brand-secondary shadow-brand-card">
                    {index + 1}
                  </span>
                  <div className="group rounded-3xl bg-brand-glass border border-brand-soft p-5 sm:p-6 shadow-brand-card transition-all duration-500 hover:bg-white/75">
                    <h3 className="text-2xl font-bold text-brand-primary mb-4">{phase.title}</h3>
                    <div className="flex flex-col gap-3 text-brand-muted text-base leading-relaxed">
                      <p className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80 border border-brand-soft text-brand-secondary">
                          <CalendarIcon className="icon-sm" title="التاريخ" size={20} />
                        </span>
                        <span className="font-medium text-brand-primary">التاريخ:</span>
                        <span>{phase.date}</span>
                      </p>
                      <p className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/80 border border-brand-soft text-brand-secondary">
                          <BoxIcon className="icon-sm" title="الكمية" size={20} />
                        </span>
                        <span className="font-medium text-brand-primary">الكمية:</span>
                        <span>{phase.quantity}</span>
                      </p>
                      <p className="leading-relaxed text-brand-muted group-hover:text-brand-primary transition-colors duration-300">{phase.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden rounded-4xl border border-brand-soft bg-brand-glass p-6 md:p-8 lg:p-12 shadow-brand-card">
          <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-transparent to-transparent"></div>
          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="space-y-3 max-w-3xl">
              <h3 className="text-2xl font-semibold text-brand-primary">رؤية المساهمة</h3>
              <p className="text-base md:text-lg text-brand-muted leading-relaxed">ترتكز المساهمة على بناء منظومة توزيع شفافة تُعيد تعريف تجربة الدعم الغذائي عبر الجمع بين التوثيق المرئي والتخطيط الدقيق وتفعيل قنوات المتابعة الذكية.</p>
            </div>
            <div className="inline-flex items-center gap-3 rounded-full bg-white/80 border border-brand-soft px-6 py-3 text-sm font-medium text-brand-secondary shadow-brand-card">
              <PinDotIcon className="icon-dot" title="انطلاق" />
              توازن بين الأثر الاجتماعي والحوكمة
            </div>
          </div>
        </section>

        <DocumentationGallery />
      </div>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;600;700&display=swap');

        * {
          font-family: 'Tajawal', sans-serif;
        }

        .decorative-layer {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
        }

        .decorative-orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(60px);
          opacity: 0.35;
        }

        .orb-one {
          width: 360px;
          height: 360px;
          top: -140px;
          right: -140px;
          background: radial-gradient(circle, rgba(200, 157, 58, 0.3), transparent 65%);
        }

        .orb-two {
          width: 300px;
          height: 300px;
          bottom: 12%;
          left: -130px;
          background: radial-gradient(circle, rgba(30, 109, 59, 0.24), transparent 70%);
        }

        .orb-three {
          width: 220px;
          height: 220px;
          top: 40%;
          right: 18%;
          background: radial-gradient(circle, rgba(148, 163, 184, 0.24), transparent 65%);
        }

        .chart-container {
          position: relative;
          width: 100%;
          height: 420px;
        }

        @media (max-width: 768px) {
          .chart-container {
            height: 320px;
          }

          .orb-one {
            width: 260px;
            height: 260px;
            top: -120px;
            right: -120px;
          }

          .orb-two {
            width: 220px;
            height: 220px;
            bottom: 16%;
            left: -120px;
          }

          .orb-three {
            width: 200px;
            height: 200px;
            top: 48%;
            right: 8%;
          }
        }
      `}</style>
    </main>
  );
};

export default HousingReport;
